<template>
  <MainLayout>
    <div class="container">
      <div class="box">
        <h2>Rate Plans Calendar</h2>
        <hr />

        <!-- Step 1: Point of Sale & Rate Plan Selection -->
        <b-form @submit.prevent="loadZones">
          <b-form-row>
            <b-form-group class="col-md-3" label="Point of Sale">
              <multiselect
                v-model="selectedPointOfSale"
                :options="pointOfSalesOpts"
                :multiple="false"
                label="text"
                track-by="value"
                placeholder="Select Point of Sale"
                @input="loadRatePlans"
              >
                <template slot="option" slot-scope="props">
                  <div>
                    <strong>{{ props.option.text }}</strong>
                    <br />
                    <small style="opacity: 0.7;" v-if="props.option.domain">{{ props.option.domain }}</small>
                  </div>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group class="col-md-3" label="Rate Plan">
              <multiselect
                v-model="selectedRatePlan"
                :options="ratePlans"
                :multiple="false"
                :disabled="!selectedPointOfSale"
                label="name"
                track-by="id"
                placeholder="Select Rate Plan"
                @input="loadZones"
              >
                <template slot="option" slot-scope="props">
                  <div>
                    <strong>{{ props.option.name }}</strong>
                    <br />
                    <small>{{ props.option.zonedAreaName }} - {{ props.option.currency }}</small>
                  </div>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group class="col-md-2" label="Origin Zone">
              <multiselect
                v-model="selectedFromZone"
                :options="zones"
                :multiple="false"
                :disabled="!selectedRatePlan"
                label="name"
                track-by="id"
                placeholder="Select Origin"
              >
                <template slot="option" slot-scope="props">
                  <div>
                    {{ props.option.name }}
                    <small v-if="props.option.code"> ({{ props.option.code }})</small>
                  </div>
                </template>
              </multiselect>
            </b-form-group>

            <b-form-group class="col-md-2" label="Ticket Type">
              <b-form-select
                v-model="ticketType"
                :options="ticketTypeOptions"
                size="sm"
              />
            </b-form-group>

            <b-form-group class="col-md-2" label="Service Time">
              <b-form-input
                v-model="serviceTime"
                type="time"
                size="sm"
              />
              <small class="text-muted">{{ ticketType === 'roundtrip' ? 'Applies to both trips' : 'For the trip' }}</small>
            </b-form-group>

            <b-form-group class="col-md-2" label="Start Date">
              <b-form-datepicker
                v-model="startDate"
                locale="es"
                size="sm"
                reset-button
              />
            </b-form-group>

            <b-form-group class="col-md-1" label="Days">
              <b-form-input
                v-model.number="days"
                type="number"
                min="7"
                max="30"
                size="sm"
              />
            </b-form-group>

            <b-form-group class="col-md-1" label=".">
              <b-button
                @click="loadCalendar"
                variant="primary"
                size="sm"
                :disabled="!selectedFromZone"
                block
              >
                <md-icon icon="search" /> Load
              </b-button>
            </b-form-group>
          </b-form-row>
        </b-form>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner type="grow" label="Loading..."></b-spinner>
          <p class="mt-3">Loading calendar data...</p>
        </div>

        <!-- Calendar Table -->
        <div v-else-if="calendarData && calendarData.dateRange && calendarData.dateRange.length > 0" class="mt-4">
          <div class="mb-3">
            <h4>
              Rateplan:
              {{ calendarData.ratePlanName }}
              <b-badge variant="info">{{ calendarData.currency }}</b-badge>
            </h4>
            <p class="text-muted" v-if="calendarData.fromZone">
              From: <strong>{{ calendarData.fromZone.name }}</strong>
            </p>
          </div>

          <div class="table-responsive calendar-table-wrapper">
            <table class="table table-sm table-bordered calendar-table">
              <thead class="thead-light">
                <tr>
                  <th class="sticky-col vehicle-col">Vehicle</th>
                  <th class="sticky-col pax-col">Pax</th>
                  <th v-for="date in calendarData.dateRange" :key="date" class="date-col">
                    <div class="date-header">
                      <div class="date-day">{{ formatDateDay(date) }}</div>
                      <div class="date-month">{{ formatDateMonth(date) }}</div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in flattenedRoutes"
                  :key="row.key"
                  :class="row.type === 'header' ? 'destination-header-row' : 'service-type-row'"
                >
                  <!-- Destination Header Row -->
                  <template v-if="row.type === 'header'">
                    <td colspan="2" class="destination-header destination-header-sticky">
                      <md-icon icon="place" style="font-size: 16px; vertical-align: middle;"></md-icon>
                      <strong>{{ row.toZoneName }}</strong>
                    </td>
                    <td :colspan="calendarData.dateRange.length" class="destination-header destination-header-scroll"></td>
                  </template>

                  <!-- Service Type Row -->
                  <template v-else>
                    <td class="sticky-col vehicle-col">
                      <div>{{ row.route.serviceTypeName }}</div>
                      <span
                        :class="row.route.serviceType === 'private' ? 'service-tag service-tag-private' : 'service-tag service-tag-shared'"
                      >
                        {{ row.route.serviceType }}
                      </span>
                    </td>
                    <td class="sticky-col pax-col text-center">{{ row.route.maxPax }}</td>
                    <td
                      v-for="date in calendarData.dateRange"
                      :key="date"
                      class="price-cell"
                      :class="{
                        'clickable-cell': row.route.prices[date] && row.route.prices[date].isActive,
                        'active-cell': isActiveCell(row.key, date)
                      }"
                      @click="openContextMenu($event, row.route, date, row.key)"
                    >
                      <div v-if="row.route.prices[date] && row.route.prices[date].isActive" class="price-content">
                        <div
                          v-if="row.route.prices[date].promotionApplied && row.route.prices[date].promotionApplied.length"
                          class="original-price"
                        >
                          {{ getOriginalPrice(row.route.prices[date].promotionApplied) }}
                        </div>
                        <div
                          class="price-amount"
                          :class="{ 'has-discount': row.route.prices[date].hasDiscount }"
                        >
                          {{ formatPrice(row.route.prices[date].price) }}
                          <span
                            v-if="row.route.prices[date].promotionApplied && row.route.prices[date].promotionApplied.length"
                            class="promo-percent-text"
                            :class="{
                              'promo-cheaper': getPromotionPercent(row.route.prices[date].promotionApplied, row.route.prices[date].price).startsWith('-'),
                              'promo-expensive': getPromotionPercent(row.route.prices[date].promotionApplied, row.route.prices[date].price).startsWith('+')
                            }"
                            :title="getPromotionNames(row.route.prices[date].promotionApplied)"
                          >
                            {{ getPromotionPercent(row.route.prices[date].promotionApplied, row.route.prices[date].price) }}
                          </span>
                        </div>
                      </div>
                      <div v-else class="inactive-cell">-</div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Zone Management -->
          <div class="mt-3 zone-management">
            <b-row class="align-items-center">
              <b-col class="col-md-12">
                <div class="d-flex align-items-center justify-content-end">
                  <label class="mb-0 mr-2"><small>Add Destination:</small></label>
                  <multiselect
                    v-model="zoneToAdd"
                    :options="availableZonesToAdd"
                    label="name"
                    track-by="id"
                    placeholder="Select zone to add"
                    class="zone-selector"
                    @input="addZone"
                  />
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Legend -->
          <div class="mt-3">
            <b-row>
              <b-col class="col-md-6">
                <small>
                  <b-badge variant="primary">{{ ticketType === 'oneway' ? 'OW' : 'RT' }}</b-badge>
                  {{ ticketType === 'oneway' ? 'One-way' : 'Round-trip' }} prices |
                  <span class="has-discount">Red prices</span> have discounts
                </small>
              </b-col>
              <b-col class="col-md-6 text-right">
                <small class="text-muted">
                  Showing {{ selectedToZones.length }} destination(s) for {{ days }} days
                </small>
              </b-col>
            </b-row>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-5 text-muted">
          <md-icon icon="event" style="font-size: 48px;" />
          <p class="mt-3">Select a Point of Sale, Rate Plan, and Origin Zone to view calendar</p>
        </div>
      </div>
    </div>

    <!-- Context Menu -->
    <div
      v-if="contextMenu.visible && contextMenu.route"
      class="context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      @click.stop
    >
      <div class="context-menu-header">
        <strong>{{ formatDateFull(contextMenu.date) }}</strong>
        <br />
        <small class="text-muted">{{ contextMenu.route.toZoneName }} - {{ contextMenu.route.serviceTypeName }}</small>
      </div>
      <div class="context-menu-divider"></div>
      <div class="context-menu-items">
        <div class="context-menu-item" @click="openRatePlanModal">
          <md-icon icon="edit" class="menu-icon" />
          <span>Edit Rate Plan</span>
        </div>
        <div class="context-menu-item" @click="viewPriceDetails">
          <md-icon icon="attach_money" class="menu-icon" />
          <span>View Price Details</span>
        </div>
        <template v-if="selectedPointOfSale && selectedPointOfSale.value === 535">
          <div class="context-menu-divider"></div>
          <div class="context-menu-item" @click="openAnalyzeMarketplace">
            <md-icon icon="analytics" class="menu-icon" />
            <span>Analyze Marketplace Prices</span>
          </div>
        </template>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="copyPriceInfo">
          <md-icon icon="content_copy" class="menu-icon" />
          <span>Copy Price Info</span>
        </div>
        <div class="context-menu-item text-danger" @click="closeContextMenu">
          <md-icon icon="close" class="menu-icon" />
          <span>Close</span>
        </div>
      </div>
    </div>

    <!-- Click outside to close context menu -->
    <div
      v-if="contextMenu.visible"
      class="context-menu-backdrop"
      @click="closeContextMenu"
    ></div>

    <!-- Price Details Modal -->
    <b-modal
      ref="priceDetailsModal"
      title="Price Details"
      size="lg"
      hide-footer
    >
      <div v-if="contextMenu.route && calendarData && calendarData.fromZone">
        <!-- Service Summary Table -->
        <b-card class="mb-3">
          <b-card-header class="bg-light">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0 font-weight-bold">Service Information</h6>
              <b-badge :variant="contextMenu.priceData.isActive ? 'success' : 'danger'" class="px-2 py-1">
                {{ contextMenu.priceData.isActive ? 'Active' : 'Inactive' }}
              </b-badge>
            </div>
          </b-card-header>
          <b-card-body class="p-0">
            <table class="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td class="text-muted" style="width: 30%;">
                    <md-icon icon="calendar_today" style="font-size: 14px; vertical-align: middle;" class="mr-1"></md-icon>
                    Date
                  </td>
                  <td class="font-weight-semibold">{{ formatDateFull(contextMenu.date) }}</td>
                </tr>
                <tr>
                  <td class="text-muted">
                    <md-icon icon="route" style="font-size: 14px; vertical-align: middle;" class="mr-1"></md-icon>
                    Route
                  </td>
                  <td class="font-weight-semibold">
                    {{ calendarData.fromZone.name }} → {{ contextMenu.route.toZoneName }}
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">
                    <md-icon icon="directions_car" style="font-size: 14px; vertical-align: middle;" class="mr-1"></md-icon>
                    Vehicle
                  </td>
                  <td class="font-weight-semibold">
                    {{ contextMenu.route.serviceTypeName }}
                    <b-badge
                      :variant="contextMenu.route.serviceType === 'private' ? 'primary' : 'success'"
                      class="ml-2 text-uppercase"
                      style="font-size: 0.7rem;"
                    >
                      {{ contextMenu.route.serviceType }}
                    </b-badge>
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">
                    <md-icon icon="people" style="font-size: 14px; vertical-align: middle;" class="mr-1"></md-icon>
                    Capacity
                  </td>
                  <td class="font-weight-semibold">{{ contextMenu.route.maxPax }} passengers</td>
                </tr>
                <tr>
                  <td class="text-muted">
                    <md-icon icon="attach_money" style="font-size: 14px; vertical-align: middle;" class="mr-1"></md-icon>
                    {{ ticketType === 'oneway' ? 'One-way' : 'Round-trip' }} Price
                  </td>
                  <td>
                    <span class="h5 mb-0 font-weight-bold" :class="contextMenu.priceData.hasDiscount ? 'text-danger' : 'text-primary'">
                      {{ formatPrice(contextMenu.priceData.price) }}
                    </span>
                    <span v-if="contextMenu.priceData.hasDiscount" class="discount-tag ml-2">
                      <md-icon icon="local_offer" style="font-size: 12px; vertical-align: middle;"></md-icon>
                      Discount
                    </span>
                  </td>
                </tr>
                <tr v-if="contextMenu.priceData.promotionApplied && contextMenu.priceData.promotionApplied.length">
                  <td class="text-muted">
                    <md-icon icon="local_offer" style="font-size: 14px; vertical-align: middle;" class="mr-1"></md-icon>
                    Promotion{{ contextMenu.priceData.promotionApplied.length > 1 ? 's' : '' }} Applied
                  </td>
                  <td>
                    <div v-for="(promo, idx) in contextMenu.priceData.promotionApplied" :key="idx" :class="{ 'mt-2': idx > 0 }">
                      <div class="font-weight-semibold text-success">{{ promo.name }}</div>
                      <small class="text-muted" v-if="promo.amounts && promo.amounts.length">
                        Original: {{ promo.amounts[0].original }}
                        → New: {{ promo.amounts[0].nuevo }}
                      </small>
                      <div v-if="promo.comments" class="mt-1">
                        <small class="text-muted">
                          <md-icon icon="info" style="font-size: 12px; vertical-align: middle;"></md-icon>
                          {{ promo.comments }}
                        </small>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-card-body>
        </b-card>

        <!-- Promotions Section -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            <md-icon icon="local_offer" class="mr-2"></md-icon>
            Active Promotions for this Date
          </h6>
          <b-button
            variant="success"
            size="sm"
            @click="openQuickPromotionFromModal"
          >
            <md-icon icon="add" style="font-size: 16px; vertical-align: middle;"></md-icon>
            Quick Add Promotion
          </b-button>
        </div>

        <!-- Expired promotions filter -->
        <b-form-checkbox
          v-model="contextMenu.showExpired"
          size="sm"
          class="mb-2"
          @change="loadPromotionsForDate"
        >
          <small>Show expired promotions</small>
        </b-form-checkbox>

        <div v-if="contextMenu.loadingPromotions" class="text-center py-3">
          <b-spinner small></b-spinner>
          <p class="mb-0 mt-2 small text-muted">Loading promotions...</p>
        </div>

        <div v-else-if="contextMenu.promotions && contextMenu.promotions.length > 0">
          <b-card
            v-for="promo in contextMenu.promotions"
            :key="promo.id"
            class="mb-2"
            :border-variant="isPromotionApplied(promo) ? 'success' : isPromotionExpired(promo) ? 'secondary' : 'primary'"
            :class="{ 'opacity-60': isPromotionExpired(promo), 'border-applied': isPromotionApplied(promo) }"
          >
            <b-row>
              <b-col class="col-8">
                <h6 class="mb-1" :class="{ 'text-muted': isPromotionExpired(promo) }">{{ promo.name }}</h6>
                <small class="text-muted">Code: {{ promo.code }}</small>
              </b-col>
              <b-col class="col-4 text-right">
                <b-badge v-if="isPromotionApplied(promo)" variant="success">Applied</b-badge>
                <b-badge v-else-if="isPromotionExpired(promo)" variant="secondary">Expired</b-badge>
                <b-badge v-else variant="success">Active</b-badge>
              </b-col>
            </b-row>
            <hr class="my-2" />
            <div class="small">
              <div v-if="promo.description" class="mb-2">
                <strong>Description:</strong> {{ promo.description }}
              </div>
              <div>
                <strong>Type:</strong>
                <span v-if="promo.rateModifierType === 'percent'">Percentage</span>
                <span v-else-if="promo.rateModifierType === 'amount'">Fixed Amount</span>
                <span v-else-if="promo.rateModifierType === 'fixed_rate'">Fixed Rate</span>
              </div>
              <div v-if="promo.priority">
                <strong>Priority:</strong> {{ promo.priority }}
              </div>
              <div v-if="promo.scheduleStart && promo.scheduleEnd" class="mt-1">
                <md-icon icon="schedule" style="font-size: 14px; vertical-align: middle;" class="mr-1"></md-icon>
                <strong>Schedule:</strong> {{ formatScheduleTime(promo.scheduleStart, promo.scheduleEnd) }}
              </div>
              <!-- Dates: Travel & Booking Windows -->
              <div v-if="promo.dates" class="mt-2">
                <div v-if="promo.dates.travelWindow && promo.dates.travelWindow.length">
                  <strong>Travel Window:</strong>
                  <span v-for="(range, i) in promo.dates.travelWindow" :key="'tw-' + i" class="ml-1">
                    <span v-if="i > 0">/</span>
                    {{ formatDateShort(range.start) }} – {{ formatDateShort(range.end) }}
                  </span>
                </div>
                <div v-if="promo.dates.travelWindowBlockout && promo.dates.travelWindowBlockout.length" class="text-danger">
                  <strong>Travel Blockout:</strong>
                  <span v-for="(range, i) in promo.dates.travelWindowBlockout" :key="'twb-' + i" class="ml-1">
                    <span v-if="i > 0">/</span>
                    {{ formatDateShort(range.start) }} – {{ formatDateShort(range.end) }}
                  </span>
                </div>
                <div v-if="promo.dates.bookingWindow && promo.dates.bookingWindow.length">
                  <strong>Booking Window:</strong>
                  <span v-for="(range, i) in promo.dates.bookingWindow" :key="'bw-' + i" class="ml-1">
                    <span v-if="i > 0">/</span>
                    {{ formatDateShort(range.start) }} – {{ formatDateShort(range.end) }}
                  </span>
                </div>
                <div v-if="promo.dates.bookingWindowBlockout && promo.dates.bookingWindowBlockout.length" class="text-danger">
                  <strong>Booking Blockout:</strong>
                  <span v-for="(range, i) in promo.dates.bookingWindowBlockout" :key="'bwb-' + i" class="ml-1">
                    <span v-if="i > 0">/</span>
                    {{ formatDateShort(range.start) }} – {{ formatDateShort(range.end) }}
                  </span>
                </div>
              </div>

              <div v-if="promo.settings && promo.settings.length">
                <div
                  class="clickable-routes"
                  @click="togglePromotionRoutes(promo.id)"
                  style="cursor: pointer; color: #007bff;"
                >
                  <strong>Applies to:</strong> {{ promo.settings.length }} route(s)
                  <md-icon :icon="isPromotionExpanded(promo.id) ? 'expand_less' : 'expand_more'" class="ml-1" style="font-size: 16px;"></md-icon>
                </div>

                <!-- Route Details (Expandable) -->
                <b-collapse :visible="isPromotionExpanded(promo.id)" class="mt-2">
                  <div class="routes-detail-container">
                    <div
                      v-for="(setting, idx) in promo.settings"
                      :key="idx"
                      class="route-detail-item"
                    >
                      <div class="route-header">
                        <md-icon icon="route" class="mr-1" style="font-size: 14px;"></md-icon>
                        <strong>Route {{ idx + 1 }}</strong>
                      </div>
                      <div class="route-info">
                        <div>
                          <span class="route-label">Service:</span>
                          <span>{{ getServiceTypeName(setting) }}</span>
                        </div>
                        <div>
                          <span class="route-label">From:</span>
                          <span>{{ getZoneName(setting, 'from') }}</span>
                        </div>
                        <div>
                          <span class="route-label">To:</span>
                          <span>{{ getZoneName(setting, 'to') }}</span>
                        </div>
                        <div>
                          <span class="route-label">Ticket:</span>
                          <span>{{ getTicketTypeName(setting.ticket) }}</span>
                        </div>
                        <div>
                          <span class="route-label">Amount:</span>
                          <span :class="getModifierClass(setting)">
                            {{ formatModifier(setting, promo.rateModifierType) }}
                          </span>
                        </div>
                        <div v-if="setting.applyReturn">
                          <b-badge variant="info" class="mt-1">Applies to Return</b-badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-collapse>
              </div>
            </div>
          </b-card>
        </div>

        <b-alert v-else show variant="info" class="mb-0">
          <small>
            <md-icon icon="info" class="mr-1"></md-icon>
            No promotions active for this date
          </small>
        </b-alert>

        <div class="text-right mt-3">
          <b-button variant="secondary" size="sm" @click="$refs.priceDetailsModal.hide()">
            Close
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Marketplace Analysis Modal -->
    <b-modal
      ref="marketplaceAnalysisModal"
      title="Analyze Marketplace Prices"
      size="lg"
    >
      <b-overlay :show="marketplaceAnalysis.loading" rounded="sm">
        <b-form @submit.prevent="analyzeMarketplace">
          <b-row>
            <b-col class="col-md-6 mb-3">
              <b-form-group label="Service Date *" label-class="font-weight-semibold small">
                <b-form-datepicker
                  v-model="marketplaceAnalysis.form.serviceDate"
                  locale="es"
                  size="sm"
                  required
                  :min="new Date().toISOString().split('T')[0]"
                />
              </b-form-group>
            </b-col>
            <b-col class="col-md-6 mb-3">
              <b-form-group label="Service Time" label-class="font-weight-semibold small">
                <b-form-input
                  v-model="marketplaceAnalysis.form.serviceTime"
                  type="time"
                  size="sm"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col class="col-md-4 mb-3">
              <b-form-group label="Adults *" label-class="font-weight-semibold small">
                <b-form-input
                  v-model.number="marketplaceAnalysis.form.adults"
                  type="number"
                  min="1"
                  max="20"
                  size="sm"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col class="col-md-4 mb-3">
              <b-form-group label="Children" label-class="font-weight-semibold small">
                <b-form-input
                  v-model.number="marketplaceAnalysis.form.children"
                  type="number"
                  min="0"
                  max="20"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col class="col-md-4 mb-3">
              <b-form-group label="Infants" label-class="font-weight-semibold small">
                <b-form-input
                  v-model.number="marketplaceAnalysis.form.infants"
                  type="number"
                  min="0"
                  max="10"
                  size="sm"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col class="col-md-4 mb-3">
              <b-form-group label="Trip Type *" label-class="font-weight-semibold small">
                <b-form-select
                  v-model="marketplaceAnalysis.form.tripType"
                  :options="[
                    { value: 'oneway', text: 'One-way' },
                    { value: 'roundtrip', text: 'Round-trip' }
                  ]"
                  size="sm"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col class="col-md-4 mb-3">
              <b-form-group label="Language" label-class="font-weight-semibold small">
                <b-form-select
                  v-model="marketplaceAnalysis.form.lang"
                  :options="[
                    { value: 'en', text: 'English' },
                    { value: 'es', text: 'Spanish' }
                  ]"
                  size="sm"
                />
              </b-form-group>
            </b-col>
            <b-col class="col-md-4 mb-3">
              <b-form-group label="Marketplace Markup %" label-class="font-weight-semibold small">
                <b-form-input
                  v-model.number="marketplaceAnalysis.form.marketplacePercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  size="sm"
                />
                <small class="text-muted">% added to your net rates</small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-alert show variant="info" class="mt-3 mb-0">
            <small>
              <strong>Route:</strong> {{ (contextMenu.route && calendarData && calendarData.fromZone) ? calendarData.fromZone.name : '' }}
              → {{ contextMenu.route ? contextMenu.route.toZoneName : '' }}
              <br />
              <strong>Currency:</strong> {{ calendarData ? calendarData.currency : '' }}
            </small>
          </b-alert>
        </b-form>
      </b-overlay>

      <template #modal-footer>
        <b-button variant="secondary" size="sm" @click="closeAnalysisModal">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          @click="analyzeMarketplace(false)"
          :disabled="marketplaceAnalysis.loading"
        >
          <md-icon icon="analytics" /> Analyze
        </b-button>
      </template>
    </b-modal>

    <!-- Marketplace Results Modal -->
    <b-modal
      ref="marketplaceResultsModal"
      title="Marketplace Price Analysis"
      size="xl"
      hide-footer
      @hidden="resetMarketplaceForm"
    >
      <b-overlay :show="marketplaceAnalysis.loading" rounded="sm">
        <div v-if="marketplaceAnalysis.results">
        <!-- Analysis Context Info -->
        <b-alert show variant="info" class="mb-3">
          <b-row class="align-items-center">
            <b-col cols="12" md="4">
              <div>
                <md-icon icon="calendar_today" style="font-size: 16px; vertical-align: middle;" class="mr-1"></md-icon>
                <strong>Service Date:</strong>
                <span class="ml-2">{{ formatDateFull(marketplaceAnalysis.form.serviceDate) }}</span>
              </div>
            </b-col>
            <b-col cols="12" md="4">
              <div>
                <md-icon icon="route" style="font-size: 16px; vertical-align: middle;" class="mr-1"></md-icon>
                <strong>Route:</strong>
                <span class="ml-2" v-if="contextMenu.route && calendarData && calendarData.fromZone">
                  {{ calendarData.fromZone.name }} → {{ contextMenu.route.toZoneName }}
                </span>
              </div>
            </b-col>
            <b-col cols="12" md="4">
              <div>
                <md-icon icon="people" style="font-size: 16px; vertical-align: middle;" class="mr-1"></md-icon>
                <strong>Passengers:</strong>
                <span class="ml-2">
                  {{ marketplaceAnalysis.form.adults }} Adults
                  <span v-if="marketplaceAnalysis.form.children > 0">, {{ marketplaceAnalysis.form.children }} Children</span>
                  <span v-if="marketplaceAnalysis.form.infants > 0">, {{ marketplaceAnalysis.form.infants }} Infants</span>
                </span>
              </div>
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col cols="12" md="4">
              <div>
                <md-icon icon="schedule" style="font-size: 16px; vertical-align: middle;" class="mr-1"></md-icon>
                <strong>Service Time:</strong>
                <span class="ml-2">{{ marketplaceAnalysis.form.serviceTime || 'N/A' }}</span>
              </div>
            </b-col>
            <b-col cols="12" md="4">
              <div>
                <md-icon icon="confirmation_number" style="font-size: 16px; vertical-align: middle;" class="mr-1"></md-icon>
                <strong>Trip Type:</strong>
                <span class="ml-2">{{ marketplaceAnalysis.form.tripType === 'oneway' ? 'One-way' : 'Round-trip' }}</span>
              </div>
            </b-col>
            <b-col cols="12" md="4">
              <div>
                <md-icon icon="attach_money" style="font-size: 16px; vertical-align: middle;" class="mr-1"></md-icon>
                <strong>Currency:</strong>
                <span class="ml-2">{{ calendarData ? calendarData.currency : 'USD' }}</span>
              </div>
            </b-col>
          </b-row>
        </b-alert>

        <!-- Marketplace Markup Control -->
        <b-card class="mb-3 bg-light border-primary">
          <b-row class="align-items-center">
            <b-col cols="12" md="4">
              <b-form-group label="Marketplace Markup %" label-class="font-weight-bold mb-2" class="mb-0">
                <b-input-group>
                  <b-form-input
                    v-model.number="marketplaceAnalysis.form.marketplacePercent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    size="lg"
                    class="font-weight-bold text-center"
                  />
                  <b-input-group-append>
                    <b-input-group-text class="bg-primary text-white font-weight-bold">%</b-input-group-text>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="8">
              <div class="d-flex align-items-center h-100">
                <div class="flex-grow-1">
                  <div class="mb-1">
                    <small class="text-muted font-weight-semibold">YOUR LOWEST NET RATE:</small>
                    <span class="ml-2 h5 mb-0 text-primary font-weight-bold" v-if="marketplaceAnalysis.results.ourPrices && marketplaceAnalysis.results.ourPrices.length">
                      {{ formatCurrency(getLowestNetRate()) }}
                    </span>
                  </div>
                  <div>
                    <small class="text-muted font-weight-semibold">WITH {{ marketplaceAnalysis.form.marketplacePercent }}% MARKUP:</small>
                    <span class="ml-2 h4 mb-0 text-success font-weight-bold" v-if="marketplaceAnalysis.results.ourPrices && marketplaceAnalysis.results.ourPrices.length">
                      {{ formatCurrency(getLowestPriceWithMarkup()) }}
                    </span>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col cols="12">
              <small class="text-muted">
                <md-icon icon="info" style="font-size: 14px; vertical-align: middle;"></md-icon>
                Adjust the markup percentage to see how competitive your rates are in real-time
              </small>
            </b-col>
          </b-row>
        </b-card>

        <!-- Our Prices -->
        <b-card class="mb-3" v-if="marketplaceAnalysis.results.ourPrices && marketplaceAnalysis.results.ourPrices.length">
          <b-card-header class="bg-success text-white">
            <h5 class="mb-0">
              <md-icon icon="local_offer" class="mr-2" />
              Our Net Rates ({{ marketplaceAnalysis.results.ourPrices.length }} options)
            </h5>
          </b-card-header>
          <b-card-body>
            <b-alert show variant="info" class="mb-3">
              <small>
                <strong>Marketplace Markup:</strong> {{ marketplaceAnalysis.form.marketplacePercent }}%
                <span class="ml-2 text-muted">| This markup will be added to your net rates for marketplace comparison</span>
              </small>
            </b-alert>
            <b-table
              :items="marketplaceAnalysis.results.ourPrices"
              :fields="[
                { key: 'serviceType.name', label: 'Service Type' },
                { key: 'serviceType.maxPax', label: 'Max Pax' },
                { key: 'price', label: 'Net Rate' },
                { key: 'priceWithMarkup', label: 'With Markup (+' + marketplaceAnalysis.form.marketplacePercent + '%)' },
                { key: 'hasPromotion', label: 'Promotion' }
              ]"
              striped
              hover
              small
            >
              <template #cell(price)="row">
                {{ formatCurrency(getItemPrice(row.item)) }}
              </template>
              <template #cell(priceWithMarkup)="row">
                {{ formatCurrency(calculatePriceWithMarkup(getItemPrice(row.item))) }}
              </template>
              <template #cell(hasPromotion)="row">
                <b-badge v-if="row.item.promotionApplied && row.item.promotionApplied.length" variant="success">
                  Yes
                </b-badge>
                <b-badge v-else variant="secondary">No</b-badge>
              </template>
            </b-table>
          </b-card-body>
        </b-card>

        <!-- Marketplace Prices -->
        <b-card class="mb-3" v-if="marketplaceAnalysis.results.marketplacePrices">
          <b-card-header class="bg-info text-white">
            <h5 class="mb-0">
              <md-icon icon="store" class="mr-2" />
              Marketplace Prices Comparison
              <span v-if="marketplaceAnalysis.results.marketplacePrices.prices && marketplaceAnalysis.results.marketplacePrices.prices.length">
                ({{ marketplaceAnalysis.results.marketplacePrices.prices.length }} options)
              </span>
            </h5>
          </b-card-header>
          <b-card-body>
            <div v-if="marketplaceAnalysis.results.marketplacePrices.prices && marketplaceAnalysis.results.marketplacePrices.prices.length">
              <b-alert show variant="warning" class="mb-3">
                <small>
                  <md-icon icon="info" style="font-size: 14px; vertical-align: middle;" class="mr-1"></md-icon>
                  <strong>Your Lowest Rate with {{ marketplaceAnalysis.form.marketplacePercent }}% markup:</strong>
                  <span v-if="marketplaceAnalysis.results.ourPrices && marketplaceAnalysis.results.ourPrices.length">
                    {{ formatCurrency(getLowestPriceWithMarkup()) }}
                  </span>
                </small>
              </b-alert>
              <b-table
                :items="marketplaceAnalysis.results.marketplacePrices.prices"
                :fields="[
                  { key: 'vehicle_type', label: 'Vehicle Type' },
                  { key: 'supplier', label: 'Supplier' },
                  { key: 'passengers', label: 'Passengers' },
                  { key: 'luggage', label: 'Luggage' },
                  { key: 'price.formatted', label: 'Market Price' },
                  { key: 'compareWith', label: 'Compare With' },
                  { key: 'ourRate', label: 'Your Rate w/Markup' },
                  { key: 'comparison', label: 'Status' }
                ]"
                striped
                hover
                small
              >
                <template #cell(compareWith)="row">
                  <b-form-select
                    v-model="marketplaceAnalysis.selectedComparisons[row.index]"
                    size="sm"
                    @change="updateComparison"
                  >
                    <b-form-select-option :value="null">Lowest Rate</b-form-select-option>
                    <b-form-select-option
                      v-for="(product, idx) in marketplaceAnalysis.results.ourPrices"
                      :key="idx"
                      :value="idx"
                    >
                      {{ product.serviceType.name }} ({{ product.serviceType.maxPax }} pax)
                    </b-form-select-option>
                  </b-form-select>
                </template>
                <template #cell(ourRate)="row">
                  <span v-if="marketplaceAnalysis.results.ourPrices && marketplaceAnalysis.results.ourPrices.length">
                    {{ formatCurrency(getSelectedProductPrice(row.index)) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(comparison)="row">
                  <span v-if="marketplaceAnalysis.results.ourPrices && marketplaceAnalysis.results.ourPrices.length && row.item.price && row.item.price.value">
                    <b-badge :variant="getComparisonBadgeVariant(getSelectedProductPrice(row.index), row.item.price.value)">
                      {{ getComparisonText(getSelectedProductPrice(row.index), row.item.price.value) }}
                    </b-badge>
                  </span>
                </template>
              </b-table>

              <div class="mt-3 text-center" v-if="marketplaceAnalysis.results.marketplacePrices.lowest_price">
                <b-badge variant="secondary" class="mr-2">
                  Market Lowest: {{ formatCurrency(marketplaceAnalysis.results.marketplacePrices.lowest_price) }}
                </b-badge>
                <b-badge variant="secondary">
                  Market Highest: {{ formatCurrency(marketplaceAnalysis.results.marketplacePrices.highest_price) }}
                </b-badge>
              </div>
            </div>
            <div v-else class="text-center py-3">
              <md-icon icon="store_mall_directory" style="font-size: 48px;" class="text-muted mb-3" />
              <p class="text-muted mb-0">No marketplace prices available</p>
              <small class="text-muted" v-if="marketplaceAnalysis.results.marketplacePrices.raw && marketplaceAnalysis.results.marketplacePrices.raw.data">
                {{ marketplaceAnalysis.results.marketplacePrices.raw.data.code }}
              </small>
            </div>
          </b-card-body>
        </b-card>

        <div class="d-flex justify-content-between align-items-center mt-3">
          <b-button
            variant="primary"
            size="sm"
            @click="reloadMarketplaceAnalysis"
            :disabled="marketplaceAnalysis.loading"
          >
            <b-spinner small v-if="marketplaceAnalysis.loading" class="mr-1"></b-spinner>
            <md-icon v-else icon="refresh" style="font-size: 16px; vertical-align: middle;"></md-icon>
            {{ marketplaceAnalysis.loading ? 'Reloading...' : 'Reload Comparison' }}
          </b-button>
          <b-button variant="secondary" size="sm" @click="$refs.marketplaceResultsModal.hide()">
            Close
          </b-button>
        </div>
      </div>
      </b-overlay>
    </b-modal>

    <!-- Rate Plan Modal -->
    <Single ref="ratePlanModal" :success-callback="handleModalSuccess" />

    <!-- Quick Promotion Modal -->
    <QuickPromotionModal
      v-if="selectedPointOfSale && selectedRatePlan"
      modal-id="quick-promotion-modal"
      :embedded-on="embeddedOn"
      :embedded-on-id="embeddedOnId"
      :rate-plan-id="selectedRatePlan.id"
      :sales-channel-id="embeddedOnId"
      :selected-date="contextMenu.date"
      @success="handlePromotionSuccess"
    />
  </MainLayout>
</template>

<script>
import MainLayout from "../../layout/MainLayout";
import Multiselect from "vue-multiselect";
import Single from "../../rate-plans/Single";
import QuickPromotionModal from "./QuickPromotionModal.vue";

export default {
  name: "rate-plans-calendar",
  components: { MainLayout, Multiselect, Single, QuickPromotionModal },

  data() {
    return {
      loading: false,
      loadingRatePlans: false,
      loadingZones: false,

      // LocalStorage key for saving filters
      STORAGE_KEY: 'rateplan_calendar_filters',

      // Selections
      selectedPointOfSale: null,
      selectedRatePlan: null,
      selectedFromZone: null,

      // Data
      ratePlans: [],
      zones: [], // FROM zones (origins)
      selectedToZones: [], // Selected destination zones to display
      availableToZones: [], // All available destination zones
      zoneToAdd: null, // For adding more zones
      calendarData: {
        currency: null,
        ratePlanName: null,
        fromZone: null,
        dateRange: [],
        routesByZone: {} // Store routes indexed by toZoneId
      },
      loadingZoneData: {}, // Track loading state per zone

      // Filters
      ticketType: 'oneway',
      ticketTypeOptions: [
        { value: 'oneway', text: 'One Way' },
        { value: 'roundtrip', text: 'Round Trip' }
      ],
      startDate: this.getDefaultStartDate(),
      days: 7,
      serviceTime: '09:00',

      // Active cell tracking
      activeCell: {
        routeKey: null,
        date: null
      },

      // Context Menu
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        route: null,
        date: null,
        priceData: null,
        promotions: [],
        loadingPromotions: false,
        expandedPromotions: {},
        showExpired: false
      },

      // Marketplace Analysis
      marketplaceAnalysis: {
        loading: false,
        results: null,
        selectedComparisons: {}, // Store selected product for each marketplace item
        form: {
          serviceDate: null,
          serviceTime: "09:00",
          adults: 2,
          children: 0,
          infants: 0,
          tripType: "oneway",
          lang: "en",
          marketplacePercent: 20
        }
      }
    };
  },

  computed: {
    pointOfSalesOpts() {
      return this.$root.auxVars.pointOfSalesOpts;
    },

    embeddedOn() {
      // Promotions are created at the sales-channel level for this module
      return "sales-channel";
    },

    embeddedOnId() {
      // Get sales channel ID from the selected rate plan
      // Point of Sale is just a distributor/pointer, the rate plan is controlled by the sales channel
      return this.selectedRatePlan?.salesChannelId || this.selectedRatePlan?.sales_channel_id;
    },

    groupedRoutes() {
      if (!this.calendarData || !this.calendarData.routesByZone) {
        return [];
      }

      const grouped = [];

      // Iterate through selected destination zones
      this.selectedToZones.forEach(toZoneId => {
        const routes = this.calendarData.routesByZone[toZoneId];

        if (routes && routes.length > 0) {
          const zone = this.availableToZones.find(z => z.id === toZoneId);

          grouped.push({
            toZoneId: toZoneId,
            toZoneName: zone ? zone.name : `Zone #${toZoneId}`,
            routes: routes
          });
        }
      });

      return grouped;
    },

    availableZonesToAdd() {
      // Return zones that are not currently selected
      return this.availableToZones.filter(zone =>
        !this.selectedToZones.includes(zone.id)
      );
    },

    flattenedRoutes() {
      if (!this.calendarData || !this.calendarData.routesByZone) {
        return [];
      }

      const flattened = [];

      this.groupedRoutes.forEach(destination => {
        // Add destination header row
        flattened.push({
          type: 'header',
          key: `dest-${destination.toZoneId}`,
          toZoneId: destination.toZoneId,
          toZoneName: destination.toZoneName
        });

        // Add service type rows
        destination.routes.forEach(route => {
          flattened.push({
            type: 'service',
            key: `${route.toZoneId}-${route.serviceTypeId}`,
            route: route
          });
        });
      });

      return flattened;
    }
  },

  methods: {
    getDefaultStartDate() {
      const today = new Date();
      return today.toISOString().split("T")[0];
    },

    saveFiltersToStorage() {
      try {
        const filters = {
          pointOfSale: this.selectedPointOfSale ? {
            value: this.selectedPointOfSale.value,
            text: this.selectedPointOfSale.text
          } : null,
          ratePlan: this.selectedRatePlan ? {
            id: this.selectedRatePlan.id,
            name: this.selectedRatePlan.name,
            salesChannelId: this.selectedRatePlan.salesChannelId || this.selectedRatePlan.sales_channel_id
          } : null,
          fromZone: this.selectedFromZone ? {
            id: this.selectedFromZone.id,
            name: this.selectedFromZone.name
          } : null,
          ticketType: this.ticketType,
          days: this.days,
          serviceTime: this.serviceTime
        };

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filters));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
    },

    async loadFiltersFromStorage() {
      try {
        const savedFilters = localStorage.getItem(this.STORAGE_KEY);
        if (!savedFilters) {
          return false;
        }

        const filters = JSON.parse(savedFilters);

        // Restore filters immediately
        if (filters.ticketType) {
          this.ticketType = filters.ticketType;
        }
        if (filters.days) {
          this.days = filters.days;
        }
        if (filters.serviceTime) {
          this.serviceTime = filters.serviceTime;
        }

        // Restore Point of Sale
        if (filters.pointOfSale) {
          // Wait for pointOfSalesOpts to be loaded
          await this.$nextTick();
          const pos = this.pointOfSalesOpts.find(p => p.value === filters.pointOfSale.value);
          if (pos) {
            this.selectedPointOfSale = pos;
            await this.loadRatePlans();

            // Restore Rate Plan
            if (filters.ratePlan) {
              const ratePlan = this.ratePlans.find(rp => rp.id === filters.ratePlan.id);
              if (ratePlan) {
                this.selectedRatePlan = ratePlan;
                await this.loadZones();

                // Restore From Zone
                if (filters.fromZone) {
                  const zone = this.zones.find(z => z.id === filters.fromZone.id);
                  if (zone) {
                    this.selectedFromZone = zone;
                    // Automatically load calendar with saved filters
                    await this.loadCalendar();
                  }
                }
              }
            }
          }
        }

        return true;
      } catch (error) {
        console.error('Error loading filters from localStorage:', error);
        return false;
      }
    },

    loadRatePlans() {
      if (!this.selectedPointOfSale) {
        this.ratePlans = [];
        this.zones = [];
        this.calendarData = null;
        return Promise.resolve();
      }

      this.loadingRatePlans = true;
      this.ratePlans = [];
      this.selectedRatePlan = null;
      this.zones = [];
      this.calendarData = null;

      return this.$root.api
        .get(`/point-of-sales/${this.selectedPointOfSale.value}/rate-plans-for-calendar`)
        .then(response => {
          this.ratePlans = response.data.ratePlans || [];
        })
        .catch(error => {
          this.$root.handleRequestError(error);
        })
        .finally(() => {
          this.loadingRatePlans = false;
        });
    },

    loadZones() {
      if (!this.selectedRatePlan || !this.selectedPointOfSale) {
        this.zones = [];
        this.calendarData = null;
        return Promise.resolve();
      }

      this.loadingZones = true;
      this.zones = [];
      this.selectedFromZone = null;
      this.calendarData = null;

      return this.$root.api
        .get(
          `/point-of-sales/${this.selectedPointOfSale.value}/rate-plans/${this.selectedRatePlan.id}/zones`
        )
        .then(response => {
          this.zones = response.data.zones || [];
        })
        .catch(error => {
          this.$root.handleRequestError(error);
        })
        .finally(() => {
          this.loadingZones = false;
        });
    },

    loadCalendar() {
      if (!this.selectedPointOfSale || !this.selectedRatePlan || !this.selectedFromZone) {
        this.$root.showAlert("Please select all required fields", {
          variant: "warning"
        });
        return Promise.reject(new Error('Missing required fields'));
      }

      this.loading = true;

      // Reset calendar data
      this.calendarData = {
        currency: null,
        ratePlanName: null,
        fromZone: null,
        dateRange: [],
        routesByZone: {}
      };
      this.selectedToZones = [];

      // Use zones array (same as origin zones) for destination zones
      // Filter out the selected origin zone from available destinations
      this.availableToZones = this.zones.filter(z => z.id !== this.selectedFromZone.id);

      // Auto-select first 5 destination zones
      const zonesToLoad = this.availableToZones.slice(0, 5);
      this.selectedToZones = zonesToLoad.map(z => z.id);

      // Load calendar for first 5 zones
      return this.loadCalendarData(this.selectedToZones)
        .then(() => {
          this.saveFiltersToStorage();
        })
        .catch(error => {
          this.$root.handleRequestError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadCalendarData(toZoneIds) {
      const params = {
        fromZoneId: this.selectedFromZone.id,
        startDate: this.startDate,
        days: this.days,
        ticketType: this.ticketType,
        serviceTime: this.serviceTime
      };

      // If toZoneIds provided, send as array
      // Backend: if toZoneIds present, load only those zones; if not, load all
      if (toZoneIds && toZoneIds.length > 0) {
        params.toZoneIds = toZoneIds;
      }

      return this.$root.api
        .get(
          `/point-of-sales/${this.selectedPointOfSale.value}/rate-plans/${this.selectedRatePlan.id}/calendar`,
          { params }
        )
        .then(response => {
          // First time loading - initialize calendarData
          if (!this.calendarData.dateRange || this.calendarData.dateRange.length === 0) {
            this.calendarData = {
              currency: response.data.currency,
              ratePlanName: response.data.ratePlanName,
              fromZone: response.data.fromZone || this.selectedFromZone,
              dateRange: response.data.dateRange || [],
              routesByZone: {}
            };
          }

          // Group routes by toZoneId and store
          if (response.data.routes) {
            response.data.routes.forEach(route => {
              const toZoneId = route.toZoneId;

              if (!this.calendarData.routesByZone[toZoneId]) {
                this.$set(this.calendarData.routesByZone, toZoneId, []);
              }

              this.calendarData.routesByZone[toZoneId].push(route);
            });
          }
        });
    },

    formatDateDay(dateStr) {
      const date = new Date(dateStr + "T00:00:00");
      return date.getDate();
    },

    formatDateMonth(dateStr) {
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("en-US", { month: "short" });
    },

    formatPrice(price) {
      if (price === null || price === undefined) {
        return "-";
      }
      return this.calendarData.currency + " " + parseFloat(price).toFixed(2);
    },

    getDestinationZoneName(zoneId) {
      const zone = this.availableToZones.find(z => z.id === zoneId);
      return zone ? zone.name : `Zone #${zoneId}`;
    },

    addZone() {
      if (this.zoneToAdd && !this.selectedToZones.includes(this.zoneToAdd.id)) {
        const zoneId = this.zoneToAdd.id;

        // Mark zone as loading
        this.$set(this.loadingZoneData, zoneId, true);

        // Add to selected zones
        this.selectedToZones.push(zoneId);

        // Load calendar data for this single zone
        this.loadCalendarData([zoneId])
          .then(() => {
            this.$set(this.loadingZoneData, zoneId, false);
          })
          .catch(error => {
            console.error(`Error loading zone ${zoneId}:`, error);
            this.$set(this.loadingZoneData, zoneId, false);

            // Remove from selected zones on error
            const index = this.selectedToZones.indexOf(zoneId);
            if (index > -1) {
              this.selectedToZones.splice(index, 1);
            }
          });

        // Reset selector
        this.zoneToAdd = null;
      }
    },

    removeZone(zoneId) {
      const index = this.selectedToZones.indexOf(zoneId);
      if (index > -1) {
        this.selectedToZones.splice(index, 1);
      }
    },

    openContextMenu(event, route, date, routeKey) {
      // Only open menu if the date has active prices
      if (!route.prices[date] || !route.prices[date].isActive) {
        return;
      }

      // Prevent default right-click if applicable
      event.preventDefault();
      event.stopPropagation();

      // Set active cell
      this.activeCell = {
        routeKey: routeKey,
        date: date
      };

      // Get click position
      const x = event.clientX;
      const y = event.clientY;

      // Store context menu data
      this.contextMenu = {
        visible: true,
        x: x,
        y: y,
        route: route,
        date: date,
        priceData: route.prices[date],
        showExpired: false,
        promotions: [],
        loadingPromotions: false,
        expandedPromotions: {}
      };
    },

    isActiveCell(routeKey, date) {
      return this.activeCell.routeKey === routeKey && this.activeCell.date === date;
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
    },
    openRatePlanModal() {
      // Open the rate plan modal with the selected rate plan ID
      if (this.selectedRatePlan && this.selectedRatePlan.id) {
        this.$refs.ratePlanModal.loadModel(this.selectedRatePlan.id);
      }
      this.closeContextMenu();
    },

    async viewPriceDetails() {
      await this.loadPromotionsForDate();
      this.$refs.priceDetailsModal.show();
      this.closeContextMenu();
    },

    async loadPromotionsForDate() {
      if (!this.embeddedOnId || !this.selectedRatePlan || !this.contextMenu.date) {
        this.contextMenu.promotions = [];
        return;
      }

      this.contextMenu.loadingPromotions = true;
      // Reset expanded state
      this.$set(this.contextMenu, 'expandedPromotions', {});

      try {
        const filters = {
          ratePlanId: this.selectedRatePlan.id,
          serviceDate: this.contextMenu.date,
          showExpired: this.contextMenu.showExpired
        };

        const response = await this.$root.api.get(
          `/util/promotions/sales-channel/${this.embeddedOnId}`,
          {
            params: {
              filters: JSON.stringify(filters)
            }
          }
        );

        // API returns data directly in response.data
        let promotions = response.data.data || response.data || [];
        promotions = Array.isArray(promotions) ? promotions : [];

        // Sort promotions by priority ASC (lower priority number = higher priority)
        promotions.sort((a, b) => {
          const priorityA = parseInt(a.priority) || 0;
          const priorityB = parseInt(b.priority) || 0;
          return priorityA - priorityB;
        });

        this.contextMenu.promotions = promotions;
      } catch (error) {
        console.error('Error loading promotions:', error);
        this.contextMenu.promotions = [];
      } finally {
        this.contextMenu.loadingPromotions = false;
      }
    },

    togglePromotionRoutes(promotionId) {
      // Ensure expandedPromotions object exists
      if (!this.contextMenu.expandedPromotions) {
        this.$set(this.contextMenu, 'expandedPromotions', {});
      }

      const currentState = this.contextMenu.expandedPromotions[promotionId] || false;
      this.$set(this.contextMenu.expandedPromotions, promotionId, !currentState);
    },

    isPromotionExpanded(promotionId) {
      return !!(this.contextMenu.expandedPromotions && this.contextMenu.expandedPromotions[promotionId]);
    },

    // True when this promo is one of the promotions applied to the current price
    isPromotionApplied(promo) {
      const applied = this.contextMenu.priceData && this.contextMenu.priceData.promotionApplied;
      if (!applied || !applied.length) return false;
      return applied.some(a => a.promotionId === promo.id);
    },

    // Expired when none of the bookingWindow ranges cover today
    isPromotionExpired(promo) {
      if (!promo.dates || !promo.dates.bookingWindow || !promo.dates.bookingWindow.length) {
        return false;
      }
      const today = new Date().toISOString().split("T")[0];
      return !promo.dates.bookingWindow.some(range => today >= range.start && today <= range.end);
    },

    getServiceTypeName(setting) {
      if (!setting) {
        return 'N/A';
      }

      // Check if we have enriched data from API
      if (setting.serviceTypeData && setting.serviceTypeData.name) {
        return setting.serviceTypeData.name;
      }

      // Check for "All Services" special case
      const serviceTypeId = parseInt(setting.serviceTypeId);
      if (serviceTypeId === 0) {
        return 'All Services';
      }

      // Fallback to ID display
      return `Service #${serviceTypeId}`;
    },

    getZoneName(setting, direction) {
      if (!setting) {
        return 'N/A';
      }

      const isFromZone = direction === 'from';
      const zoneData = isFromZone ? setting.fromZoneData : setting.toZoneData;
      const zoneId = parseInt(isFromZone ? setting.fromZoneId : setting.toZoneId);

      // Check if we have enriched data from API
      if (zoneData && zoneData.name) {
        return zoneData.name;
      }

      // Check for "All Zones" special case
      if (zoneId === 0) {
        return 'All Zones';
      }

      // Fallback to ID display
      return `Zone #${zoneId}`;
    },

    getTicketTypeName(ticket) {
      const ticketTypeMap = {
        'roundtrip': 'Round Trip',
        'oneway': 'One Way',
        '': 'All Tickets'
      };

      return ticketTypeMap[ticket] || ticketTypeMap[''] || 'All Tickets';
    },

    formatModifier(setting, rateModifierType) {
      if (!setting || !setting.modifierAmount) {
        return 'N/A';
      }

      const amount = parseFloat(setting.modifierAmount);
      const sign = parseInt(setting.modifierSign) || 1;
      const currency = setting.currency || '$';

      if (isNaN(amount)) {
        return 'Invalid amount';
      }

      const signSymbol = sign === 1 ? '+' : '-';
      const formattedAmount = amount.toFixed(2);

      switch (rateModifierType) {
        case 'percent':
          return `${signSymbol}${formattedAmount}%`;

        case 'fixed_rate':
          return `${currency}${formattedAmount} (Fixed)`;

        case 'amount':
          return `${signSymbol}${currency}${formattedAmount}`;

        default:
          return `${signSymbol}${currency}${formattedAmount}`;
      }
    },

    getModifierClass(setting) {
      if (!setting || !setting.modifierSign) {
        return '';
      }

      const sign = parseInt(setting.modifierSign);

      if (sign === 1) {
        return 'text-danger'; // Surcharge (add) = red
      } else if (sign === -1) {
        return 'text-success'; // Discount (subtract) = green
      }

      return '';
    },

    formatScheduleTime(startTime, endTime) {
      if (!startTime || !endTime) {
        return 'N/A';
      }

      // Convert 24h format (HH:mm) to 12h format
      const formatTime = (time) => {
        if (!time) return '';

        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const min = minutes || '00';

        if (hour === 0) {
          return `12:${min} AM`;
        } else if (hour < 12) {
          return `${hour}:${min} AM`;
        } else if (hour === 12) {
          return `12:${min} PM`;
        } else {
          return `${hour - 12}:${min} PM`;
        }
      };

      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    },

    copyPriceInfo() {
      const { route, date, priceData } = this.contextMenu;

      if (!this.calendarData || !this.calendarData.fromZone) {
        this.$root.showAlert("Calendar data not available", {
          variant: "warning"
        });
        return;
      }

      const text = `Date: ${this.formatDateFull(date)}
Route: ${this.calendarData.fromZone.name} → ${route.toZoneName}
Vehicle: ${route.serviceTypeName} (${route.serviceType})
Max Pax: ${route.maxPax}
${this.ticketType === 'oneway' ? 'One-way' : 'Round-trip'}: ${this.formatPrice(priceData.price)}
Currency: ${this.calendarData.currency}`;

      this.$root.copyToClipboard(text);
      this.$root.showAlert("Price information copied to clipboard!", {
        variant: "success"
      });

      this.closeContextMenu();
    },

    formatDateFull(dateStr) {
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    },

    formatDateShort(dateStr) {
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    },

    getPromotionNames(promotionApplied) {
      if (!promotionApplied || !Array.isArray(promotionApplied) || promotionApplied.length === 0) {
        return '';
      }

      // Join multiple promotion names with commas
      return promotionApplied.map(promo => promo.name).join(', ');
    },

    getOriginalPrice(promotionApplied) {
      if (!promotionApplied || !Array.isArray(promotionApplied) || promotionApplied.length === 0) {
        return '';
      }

      const isRoundtrip = this.ticketType === 'roundtrip';

      if (isRoundtrip) {
        // Round trip: 2 legs
        if (promotionApplied.length === 2) {
          // Sum both original prices (one for each leg)
          let total = 0;
          let currency = '';

          promotionApplied.forEach(promo => {
            if (promo.amounts && promo.amounts.length > 0) {
              const originalStr = promo.amounts[0].original;
              // Extract number from string like "28.70 USD"
              const match = originalStr.match(/([\d.]+)\s*([A-Z]+)/);
              if (match) {
                total += parseFloat(match[1]);
                currency = match[2];
              }
            }
          });

          return total > 0 ? `${total.toFixed(2)} ${currency}` : '';
        } else if (promotionApplied.length === 1) {
          // Multiply by 2 (single promotion applies to both legs)
          const promo = promotionApplied[0];
          if (promo.amounts && promo.amounts.length > 0) {
            const originalStr = promo.amounts[0].original;
            const match = originalStr.match(/([\d.]+)\s*([A-Z]+)/);
            if (match) {
              const doubled = parseFloat(match[1]) * 2;
              return `${doubled.toFixed(2)} ${match[2]}`;
            }
          }
        }
      } else {
        // One-way: just return the first promotion's original price
        const firstPromo = promotionApplied[0];
        if (firstPromo && firstPromo.amounts && firstPromo.amounts.length > 0) {
          return firstPromo.amounts[0].original;
        }
      }

      return '';
    },

    getOriginalPriceNumeric(promotionApplied) {
      const originalPriceStr = this.getOriginalPrice(promotionApplied);
      if (!originalPriceStr) return 0;

      // Extract number from string like "57.40 USD"
      const match = originalPriceStr.match(/([\d.]+)/);
      return match ? parseFloat(match[1]) : 0;
    },

    getPromotionPercent(promotionApplied, newPrice) {
      const originalPrice = this.getOriginalPriceNumeric(promotionApplied);
      if (!originalPrice || !newPrice) return '';

      const diff = ((newPrice - originalPrice) / originalPrice) * 100;

      // Format with sign
      const sign = diff > 0 ? '+' : '';
      return `${sign}${diff.toFixed(0)}%`;
    },

    formatCurrency(value) {
      if (!value && value !== 0) return "-";
      const currency = this.calendarData ? this.calendarData.currency : "USD";
      return `${currency.toUpperCase()} ${parseFloat(value).toFixed(2)}`;
    },

    getItemPrice(item) {
      // Get the trip type from the form
      const tripType = this.marketplaceAnalysis.form.tripType;

      // Get price based on trip type
      if (tripType === 'oneway' && item.oneway !== undefined && item.oneway !== null) {
        return parseFloat(item.oneway) || 0;
      }

      if (tripType === 'roundtrip' && item.roundtrip !== undefined && item.roundtrip !== null) {
        return parseFloat(item.roundtrip) || 0;
      }

      // Fallback: try generic price field
      if (item.price !== undefined && item.price !== null) {
        return parseFloat(item.price) || 0;
      }

      // Log the item structure for debugging
      console.warn('Could not find price in item for tripType:', tripType, item);
      return 0;
    },

    calculatePriceWithMarkup(price) {
      if (!price && price !== 0) return 0;
      const markup = this.marketplaceAnalysis.form.marketplacePercent || 0;
      return parseFloat(price) * (1 + markup / 100);
    },

    getPriceComparisonClass(ourPrice, marketPrice) {
      if (!ourPrice || !marketPrice) return '';
      const diff = ((ourPrice - marketPrice) / marketPrice) * 100;
      if (diff < -5) return 'text-success font-weight-bold'; // More than 5% cheaper
      if (diff > 5) return 'text-danger font-weight-bold'; // More than 5% more expensive
      return 'text-warning font-weight-bold'; // Within 5% range
    },

    getLowestNetRate() {
      if (!this.marketplaceAnalysis.results.ourPrices || !this.marketplaceAnalysis.results.ourPrices.length) {
        return 0;
      }
      const prices = this.marketplaceAnalysis.results.ourPrices.map(item => this.getItemPrice(item));
      return Math.min(...prices);
    },

    getLowestPriceWithMarkup() {
      const lowestNetRate = this.getLowestNetRate();
      if (!lowestNetRate) return 0;
      return this.calculatePriceWithMarkup(lowestNetRate);
    },

    getComparisonBadgeVariant(ourPrice, marketPrice) {
      if (!ourPrice || !marketPrice) return 'secondary';
      const diff = ((ourPrice - marketPrice) / marketPrice) * 100;
      if (diff < -5) return 'success'; // We're cheaper
      if (diff > 5) return 'danger'; // We're more expensive
      return 'warning'; // Competitive (within 5%)
    },

    getComparisonText(ourPrice, marketPrice) {
      if (!ourPrice || !marketPrice) return '-';
      const diff = ((ourPrice - marketPrice) / marketPrice) * 100;
      const diffAbs = Math.abs(diff);

      if (diff < -5) {
        return `${diffAbs.toFixed(1)}% Cheaper`;
      } else if (diff > 5) {
        return `${diffAbs.toFixed(1)}% Higher`;
      } else {
        return 'Competitive';
      }
    },

    getSelectedProductPrice(rowIndex) {
      const selectedIndex = this.marketplaceAnalysis.selectedComparisons[rowIndex];

      // If nothing selected or null, use lowest rate
      if (selectedIndex === null || selectedIndex === undefined) {
        return this.getLowestPriceWithMarkup();
      }

      // Get the selected product
      const product = this.marketplaceAnalysis.results.ourPrices[selectedIndex];
      if (!product) {
        return this.getLowestPriceWithMarkup();
      }

      // Get price and apply markup
      const price = this.getItemPrice(product);
      return this.calculatePriceWithMarkup(price);
    },

    updateComparison() {
      // Force Vue to re-render the comparison
      // The computed values will recalculate automatically
      this.$forceUpdate();
    },

    autoSelectMatchingProducts() {
      if (!this.marketplaceAnalysis.results ||
          !this.marketplaceAnalysis.results.marketplacePrices ||
          !this.marketplaceAnalysis.results.marketplacePrices.prices ||
          !this.marketplaceAnalysis.results.ourPrices) {
        return;
      }

      const marketplacePrices = this.marketplaceAnalysis.results.marketplacePrices.prices;
      const ourProducts = this.marketplaceAnalysis.results.ourPrices;

      // Clear existing selections
      this.marketplaceAnalysis.selectedComparisons = {};

      marketplacePrices.forEach((marketItem, rowIndex) => {
        // Try to extract passenger count from the marketplace item
        const marketPax = this.extractPassengerCount(marketItem);

        if (marketPax) {
          // Find matching product with same or closest passenger capacity
          const matchingProductIndex = this.findMatchingProduct(ourProducts, marketPax);

          if (matchingProductIndex !== null) {
            this.$set(this.marketplaceAnalysis.selectedComparisons, rowIndex, matchingProductIndex);
          }
        }
      });
    },

    extractPassengerCount(marketItem) {
      // Try to get passenger count from different possible fields
      if (marketItem.passengers && typeof marketItem.passengers === 'number') {
        return marketItem.passengers;
      }

      // Try parsing from string like "3 passengers" or "3"
      if (marketItem.passengers && typeof marketItem.passengers === 'string') {
        const match = marketItem.passengers.match(/\d+/);
        if (match) {
          return parseInt(match[0]);
        }
      }

      // Try from max_passengers or maxPassengers
      if (marketItem.max_passengers) {
        return parseInt(marketItem.max_passengers);
      }

      if (marketItem.maxPassengers) {
        return parseInt(marketItem.maxPassengers);
      }

      return null;
    },

    findMatchingProduct(ourProducts, targetPax) {
      // First, try to find exact match
      let exactMatch = ourProducts.findIndex(product =>
        product.serviceType && product.serviceType.maxPax === targetPax
      );

      if (exactMatch !== -1) {
        return exactMatch;
      }

      // If no exact match, find closest capacity that can accommodate
      let closestMatch = null;
      let smallestDiff = Infinity;

      ourProducts.forEach((product, index) => {
        if (product.serviceType && product.serviceType.maxPax) {
          const pax = product.serviceType.maxPax;

          // Only consider products that can accommodate the passengers
          if (pax >= targetPax) {
            const diff = pax - targetPax;
            if (diff < smallestDiff) {
              smallestDiff = diff;
              closestMatch = index;
            }
          }
        }
      });

      return closestMatch;
    },

    openAnalyzeMarketplace() {
      // Pre-fill the form with the selected date
      if (this.contextMenu.date) {
        this.marketplaceAnalysis.form.serviceDate = this.contextMenu.date;
      }

      this.$refs.marketplaceAnalysisModal.show();
      this.closeContextMenu();
    },

    async analyzeMarketplace(skipModalToggle = false) {
      // Ensure skipModalToggle is a boolean (in case event object is passed)
      skipModalToggle = skipModalToggle === true;

      if (!this.contextMenu.route || !this.calendarData || !this.calendarData.fromZone) {
        this.$root.showAlert("Missing route or calendar data", {
          variant: "danger"
        });
        return;
      }

      this.marketplaceAnalysis.loading = true;

      const payload = {
        pickupZoneId: this.calendarData.fromZone.id,
        dropoffZoneId: this.contextMenu.route.toZoneId,
        serviceDate: this.marketplaceAnalysis.form.serviceDate,
        serviceTime: this.marketplaceAnalysis.form.serviceTime,
        pointOfSaleId: this.selectedPointOfSale.value,
        currency: this.calendarData.currency,
        adults: this.marketplaceAnalysis.form.adults,
        children: this.marketplaceAnalysis.form.children,
        infants: this.marketplaceAnalysis.form.infants,
        tripType: this.marketplaceAnalysis.form.tripType,
        lang: this.marketplaceAnalysis.form.lang
      };

      // Clear results before loading to trigger re-render
      if (skipModalToggle) {
        this.marketplaceAnalysis.results = null;
      }

      try {
        const response = await this.$root.api.post("/point-of-sales/analyze-marketplace-prices", payload);

        // Check if we have data (response might not have 'success' field)
        if (response.data && (response.data.success === true || response.data.data)) {
          // Simply assign the new results - Vue will detect the change
          this.marketplaceAnalysis.results = response.data.data || response.data;

          // Auto-select matching products based on passenger count
          this.autoSelectMatchingProducts();

          // Only toggle modals if not reloading
          if (!skipModalToggle) {
            this.$refs.marketplaceAnalysisModal.hide();
            this.$nextTick(() => {
              this.$refs.marketplaceResultsModal.show();
            });
          }
        } else {
          this.$root.showAlert("Failed to analyze marketplace prices", {
            variant: "danger"
          });
        }
      } catch (error) {
        this.$root.handleRequestError(error);
      } finally {
        this.marketplaceAnalysis.loading = false;
      }
    },

    closeAnalysisModal() {
      this.$refs.marketplaceAnalysisModal.hide();
      // Reset form when manually cancelled
      this.resetMarketplaceForm();
    },

    async reloadMarketplaceAnalysis() {
      // Call analyzeMarketplace with skipModalToggle to avoid closing/opening modals
      await this.analyzeMarketplace(true);

      if (this.marketplaceAnalysis.results) {
        this.$root.showAlert("Marketplace comparison reloaded successfully", {
          variant: "success"
        });
      }
    },

    resetMarketplaceForm() {
      this.marketplaceAnalysis.form = {
        serviceDate: null,
        serviceTime: "09:00",
        adults: 2,
        children: 0,
        infants: 0,
        tripType: "oneway",
        lang: "en",
        marketplacePercent: 20
      };
      this.marketplaceAnalysis.results = null;
    },

    handleModalSuccess() {
      // Optionally refresh the calendar data after modal changes
      this.loadCalendar();
    },

    openQuickPromotionFromModal() {
      // Check if we have the required data
      if (!this.selectedPointOfSale) {
        this.$root.showAlert("Please select a Point of Sale first", {
          variant: "warning"
        });
        return;
      }

      if (!this.selectedRatePlan) {
        this.$root.showAlert("Please select a rate plan first", {
          variant: "warning"
        });
        return;
      }

      if (!this.embeddedOnId) {
        this.$root.showAlert("Sales channel ID not found in rate plan. This rate plan may not be configured correctly.", {
          variant: "warning"
        });
        console.log("Rate plan data:", this.selectedRatePlan);
        return;
      }

      // Close the price details modal first
      this.$refs.priceDetailsModal.hide();

      // Wait for modal to close, then open promotion modal
      this.$nextTick(() => {
        this.$bvModal.show("quick-promotion-modal");
      });
    },

    openQuickPromotion() {
      // Check if we have the required data
      if (!this.selectedPointOfSale) {
        this.$root.showAlert("Please select a Point of Sale first", {
          variant: "warning"
        });
        return;
      }

      if (!this.selectedRatePlan) {
        this.$root.showAlert("Please select a rate plan first", {
          variant: "warning"
        });
        return;
      }

      if (!this.embeddedOnId) {
        this.$root.showAlert("Sales channel ID not found in rate plan. This rate plan may not be configured correctly.", {
          variant: "warning"
        });
        console.log("Rate plan data:", this.selectedRatePlan);
        return;
      }

      // Open the quick promotion modal
      this.$bvModal.show("quick-promotion-modal");
      this.closeContextMenu();
    },

    async handlePromotionSuccess() {
      // Show success message
      this.$root.showAlert("Promotion saved successfully!", {
        variant: "success"
      });

      // Refresh the calendar to show updated prices with promotions
      await this.loadCalendar();

      // If we have a context date, reload promotions and reopen price details modal
      if (this.contextMenu.date) {
        await this.loadPromotionsForDate();
        this.$refs.priceDetailsModal.show();
      }
    }
  },

  async mounted() {
    // Load Point of Sales options
    await this.$root.auxVars.loadResourcesOpts({
      vm: this,
      resourceNames: ["pointOfSalesOpts"]
    });

    // Check if route has query params first (takes priority)
    const params = this.$root.getParams();
    if (params.pointOfSaleId) {
      const pos = this.pointOfSalesOpts.find(p => p.value == params.pointOfSaleId);
      if (pos) {
        this.selectedPointOfSale = pos;
        await this.loadRatePlans();
      }
    } else {
      // Try to restore last used filters from localStorage
      await this.loadFiltersFromStorage();
    }
  }
};
</script>

<style scoped>
.opacity-60 {
  opacity: 0.6;
}

.border-applied {
  border-width: 2px !important;
}

.calendar-table-wrapper {
  max-height: 600px;
  overflow: auto;
  position: relative;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.calendar-table {
  font-size: 0.85rem;
  margin-bottom: 0;
  white-space: nowrap;
  border-collapse: separate;
  border-spacing: 0;
}

.calendar-table thead th {
  position: sticky;
  top: 0;
  background-color: #fafafa;
  z-index: 10;
  border-bottom: 2px solid #e0e0e0;
  border-right: 1px solid #f0f0f0;
  font-weight: 600;
  color: #424242;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  padding: 12px 8px;
}

.sticky-col {
  position: sticky;
  left: 0;
  background-color: #fff !important;
  z-index: 5;
  border-right: 1px solid #f0f0f0;
}

.sticky-col.vehicle-col {
  left: 0;
  min-width: 180px;
  font-weight: 500;
  color: #333;
}

.sticky-col.pax-col {
  left: 180px;
  min-width: 60px;
  color: #616161;
  font-weight: 500;
}

/* Make sticky header work with sticky columns */
.calendar-table thead th.sticky-col {
  z-index: 15;
  background-color: #fafafa;
}

.date-col {
  min-width: 90px;
  text-align: center;
  vertical-align: middle;
}

.date-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.date-day {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.date-month {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #757575;
  letter-spacing: 0.5px;
}

/* Destination Header Row Styles */
.destination-header-row {
  background-color: #f5f5f5;
}

.destination-header {
  background-color: #f5f5f5 !important;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 10px 12px !important;
  color: #424242;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e0e0e0;
  border-top: 1px solid #e0e0e0;
}

.destination-header-sticky {
  position: sticky;
  left: 0;
  z-index: 11;
  border-right: none;
}

.destination-header-scroll {
  border-right: none;
}

.service-type-row {
  background-color: #fff;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;
}

.service-type-row:hover {
  background-color: #fafafa;
}

.service-type-row:hover .sticky-col {
  background-color: #fafafa !important;
}

.price-cell {
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px !important;
  min-width: 90px;
  background-color: inherit;
  transition: all 0.15s ease;
  border-right: 1px solid #f8f8f8;
}

.clickable-cell {
  cursor: pointer;
  position: relative;
}

.clickable-cell:hover {
  background-color: #e8f4fd !important;
  box-shadow: inset 0 0 0 1px #2196f3;
}

.clickable-cell:active {
  background-color: #d1e9f6 !important;
  box-shadow: inset 0 0 0 2px #1976d2;
}

.active-cell {
  background-color: #e3f2fd !important;
  box-shadow: inset 0 0 0 2px #1976d2 !important;
}

.price-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.original-price {
  font-size: 0.7rem;
  color: #999;
  text-decoration: line-through;
  font-weight: 400;
}

.price-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
}

.price-label {
  font-size: 0.7rem;
  font-weight: bold;
  color: #6c757d;
  text-transform: uppercase;
}

.price-amount {
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}

.has-discount {
  color: #d32f2f;
  font-weight: 700;
}

.inactive-cell {
  color: #e0e0e0;
  font-size: 1rem;
  font-weight: 300;
}

.badge-sm {
  font-size: 0.65rem;
  padding: 3px 8px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.service-tag {
  display: inline-block;
  font-size: 0.65rem;
  padding: 3px 8px;
  font-weight: 600;
  letter-spacing: 0.3px;
  border-radius: 4px;
  text-transform: uppercase;
}

.service-tag-private {
  color: #007bff;
  background-color: #cfe2ff;
  border: 1px solid #b6d4fe;
}

.service-tag-shared {
  color: #28a745;
  background-color: #d1e7dd;
  border: 1px solid #badbcc;
}

/* Promotion Indicator */
.promo-indicator {
  cursor: help;
  animation: pulse 2s infinite;
}

.promo-percent {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 4px;
  cursor: help;
  box-shadow: 0 1px 3px rgba(76, 175, 80, 0.3);
}

.promo-percent-text {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  margin-left: 4px;
  cursor: help;
}

.promo-cheaper {
  color: #4caf50;
}

.promo-expensive {
  color: #d32f2f;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* Context Menu Styles */
.context-menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: transparent;
}

.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 250px;
  overflow: hidden;
  animation: menuFadeIn 0.15s ease-out;
}

@keyframes menuFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.context-menu-header {
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.context-menu-header strong {
  font-size: 0.95rem;
}

.context-menu-header small {
  color: rgba(255, 255, 255, 0.9) !important;
  font-size: 0.8rem;
}

.context-menu-items {
  padding: 4px 0;
}

.context-menu-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  color: #333;
}

.context-menu-item:hover {
  background: #f5f5f5;
  padding-left: 20px;
}

.context-menu-item.text-danger:hover {
  background: #ffebee;
  color: #c62828;
}

.context-menu-item .menu-icon {
  margin-right: 12px;
  font-size: 18px;
  color: #666;
}

.context-menu-item.text-danger .menu-icon {
  color: #c62828;
}

.context-menu-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 4px 0;
}

/* Price Details Modal Styles */
.icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.bg-light-red {
  background-color: #fff5f5 !important;
}

.display-3 {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.2;
}

/* Multiselect styling */
.multiselect >>> .multiselect__option--highlight {
  background: #007bff;
}

.multiselect >>> .multiselect__option--selected {
  background: #f3f3f3;
  color: #333;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  /* Context Menu - Centered on mobile */
  .context-menu {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    max-width: 90vw;
    width: 300px;
  }

  .calendar-table {
    font-size: 0.7rem;
  }

  .sticky-col {
    background-color: #fff !important;
    background: #fff !important;
  }

  .sticky-col.vehicle-col {
    left: 0;
    min-width: 100px;
    background-color: #fff !important;
    background: #fff !important;
  }

  .sticky-col.pax-col {
    left: 100px;
    min-width: 45px;
    background-color: #fff !important;
    background: #fff !important;
  }

  .destination-header-sticky {
    font-size: 0.8rem;
    background-color: #f5f5f5 !important;
    background: #f5f5f5 !important;
  }

  .service-type-row .sticky-col {
    background-color: #fff !important;
    background: #fff !important;
  }

  .date-col {
    min-width: 70px;
  }

  .price-cell {
    min-width: 70px;
    font-size: 0.7rem;
  }

  .date-day {
    font-size: 1rem;
  }

  .date-month {
    font-size: 0.65rem;
  }

  .badge-sm {
    font-size: 0.6rem;
    padding: 1px 4px;
  }
}

@media (max-width: 576px) {
  .calendar-table {
    font-size: 0.65rem;
  }

  .sticky-col {
    background-color: #fff !important;
    background: #fff !important;
  }

  .sticky-col.vehicle-col {
    min-width: 80px;
    background-color: #fff !important;
    background: #fff !important;
  }

  .sticky-col.pax-col {
    left: 80px;
    min-width: 35px;
    background-color: #fff !important;
    background: #fff !important;
  }

  .destination-header-sticky {
    font-size: 0.75rem;
    padding: 8px !important;
    background-color: #f5f5f5 !important;
    background: #f5f5f5 !important;
  }

  .date-col {
    min-width: 60px;
  }

  .price-cell {
    min-width: 60px;
    font-size: 0.65rem;
    padding: 2px !important;
  }

  .date-day {
    font-size: 0.9rem;
  }

  .date-month {
    font-size: 0.6rem;
  }

  .badge-sm {
    display: none;
  }

  .sticky-col.vehicle-col div {
    font-size: 0.65rem;
    white-space: normal;
    line-height: 1.2;
  }
}

/* Discount Tag Styling */
.discount-tag {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #dc3545;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
}

/* Promotion Route Details Styling */
.clickable-routes:hover {
  text-decoration: underline;
}

.routes-detail-container {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 10px;
  margin-top: 8px;
}

.route-detail-item {
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 8px;
}

.route-detail-item:last-child {
  margin-bottom: 0;
}

.route-header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  font-size: 13px;
}

.route-info {
  font-size: 12px;
  line-height: 1.6;
}

.route-info > div {
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
}

.route-label {
  font-weight: 600;
  color: #6c757d;
  margin-right: 8px;
  min-width: 60px;
}

/* Zone Management Styles */
.zone-management {
  background-color: #f8f9fa;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.zone-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.zone-tag {
  display: inline-flex;
  align-items: center;
  background-color: #667eea;
  color: white;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.zone-tag:hover {
  background-color: #5568d3;
}

.zone-loading {
  opacity: 0.7;
  background-color: #9ca3db;
}

.remove-zone-icon {
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.remove-zone-icon:hover {
  opacity: 1;
}

.zone-selector {
  max-width: 300px;
  min-width: 200px;
}
</style>
